import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './MyBookings.css';
import LanguageContext from './LanguageContext';
import { myBookingsPageCopy } from './translations';
import { formatPrice } from './utils/priceFormatter';

function MyBookings() {
  const { language } = useContext(LanguageContext);
  const copy = myBookingsPageCopy[language] || myBookingsPageCopy.English;
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('Upcoming');
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail');

  // Check if user is logged in and fetch bookings
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
    if (!isLoggedIn) {
      navigate('/login', { 
        state: { message: copy.loginPrompt } 
      });
      return;
    }

    if (userId) {
      fetchBookings();
    }
  }, [navigate, userId]);

  const fetchBookings = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch bookings for the logged-in user
      // Also include bookings by email in case user_id is null
      let query = supabase
        .from('bookings')
        .select('*')
        .order('booking_date', { ascending: true })
        .order('booking_time', { ascending: true });

      // Filter by user_id if available, otherwise by email
      if (userId) {
        query = query.eq('user_id', userId);
      } else if (userEmail) {
        query = query.eq('customer_email', userEmail);
      } else {
        setIsLoading(false);
        return;
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Format bookings data
      const formattedBookings = (data || []).map(booking => ({
        id: booking.id,
        serviceName: booking.service_name,
        serviceType: booking.service_type,
        serviceId: booking.service_id,
        date: formatBookingDate(booking.booking_date),
        time: booking.booking_time,
        status: capitalizeStatus(booking.status),
        price: booking.service_price,
        duration: booking.service_duration,
        customerName: booking.customer_name,
        customerEmail: booking.customer_email,
        customerPhone: booking.customer_phone,
        userNotes: booking.user_notes,
        bookingDate: booking.booking_date // Keep raw date for filtering
      }));

      setBookings(formattedBookings);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(copy.error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatBookingDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const capitalizeStatus = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const isUpcoming = (booking) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDate = new Date(booking.bookingDate + 'T00:00:00');
    return bookingDate >= today && (booking.status === 'Confirmed' || booking.status === 'Pending');
  };

  const getFilteredBookings = () => {
    switch (activeFilter) {
      case 'Upcoming':
        return bookings.filter(b => isUpcoming(b));
      case 'Pending Confirmation':
        return bookings.filter(b => b.status === 'Pending');
      case 'Completed':
        return bookings.filter(b => b.status === 'Completed');
      default:
        return bookings;
    }
  };

  const filteredBookings = getFilteredBookings();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return '#20B2AA';
      case 'Pending':
        return '#EC4899';
      case 'Completed':
        return '#7C3AED';
      default:
        return '#C7D2FE';
    }
  };

  return (
    <div className="my-bookings-page">
      {/* Page Header */}
      <section className="bookings-header">
        <div className="container">
          <h1 className="bookings-page-title">{copy.pageTitle}</h1>
        </div>
        <div className="wave-divider-bookings"></div>
      </section>

      {/* Booking Filters */}
      <section className="bookings-filters-section">
        <div className="container">
          <div className="filter-tabs-bookings">
            {Object.entries(copy.filters).map(([key, label]) => (
              <button 
                key={key}
                className={`filter-tab-booking ${activeFilter === key ? 'active' : ''}`}
                onClick={() => setActiveFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Bookings List */}
      <section className="bookings-list-section">
        <div className="container">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner" style={{ 
                width: '50px', 
                height: '50px', 
                border: '4px solid rgba(124, 58, 237, 0.3)',
                borderTopColor: '#7C3AED',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }}></div>
              <p>{copy.loading}</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p style={{ color: '#ef4444', fontSize: '1.1rem' }}>{error}</p>
                <button 
                  className="btn-explore-services"
                  onClick={fetchBookings}
                  style={{ marginTop: '1rem' }}
                >
                  {copy.tryAgain}
                </button>
            </div>
          ) : filteredBookings.length > 0 ? (
            <div className="bookings-grid">
              {filteredBookings.map((booking) => (
                <div key={booking.id} className="booking-card">
                  <div className="booking-card-header">
                    <h3 className="booking-service-name">{booking.serviceName}</h3>
                    <div 
                      className="status-badge"
                      style={{ backgroundColor: `${getStatusColor(booking.status)}20`, borderColor: getStatusColor(booking.status) }}
                    >
                      <span 
                        className="status-dot"
                        style={{ backgroundColor: getStatusColor(booking.status) }}
                      ></span>
                      <span className="status-text">{booking.status}</span>
                    </div>
                  </div>
                  
                  <div className="booking-details">
                    {Object.entries(copy.detailLabels).map(([labelKey, labelText]) => (
                      <div key={labelKey} className="booking-detail-row">
                        <span className="detail-label">{labelText}</span>
                        <span className="detail-value">
                          {labelKey === 'type' && booking.serviceType}
                          {labelKey === 'date' && booking.date}
                          {labelKey === 'time' && booking.time}
                          {labelKey === 'duration' && booking.duration}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="booking-footer">
                    <div className="booking-price">{formatPrice(booking.price)}</div>
                    <div className="booking-actions">
                      <button 
                        className="btn-view-details-booking"
                        onClick={() => navigate(`/services/${booking.serviceId}`)}
                      >
                        {copy.viewDetails}
                      </button>
                      {booking.status === 'Confirmed' && booking.serviceType === 'On-Call' && (
                        <button className="btn-join-session">
                          {copy.joinSession}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-image">
                <img 
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&auto=format" 
                  alt="Empty bookings"
                  className="empty-wave-image"
                />
              </div>
              <h2 className="empty-state-title">{copy.emptyTitle}</h2>
              <p className="empty-state-text">
                {copy.empty[activeFilter] || copy.emptyTitle}
              </p>
              <button 
                className="btn-explore-services"
                onClick={() => navigate('/services')}
              >
                {copy.exploreServices}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Notifications Hint */}
      {filteredBookings.length > 0 && (
        <section className="notifications-hint-section">
          <div className="container">
            <div className="notifications-hint">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.89 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="currentColor"/>
              </svg>
              <span>{copy.notificationsHint}</span>
            </div>
          </div>
        </section>
      )}

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="container">
          <div className="footer-content" style={{ textAlign: 'center' }}>
            <div className="footer-column">
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>Address: 1st Floor, 124 Cleveland Street, London W1T 6PG</p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="mailto:manawave@manawave.uk" style={{ color: '#C7D2FE', textDecoration: 'none' }}>manawave@manawave.uk</a>
              </p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="tel:+447356083797" style={{ color: '#C7D2FE', textDecoration: 'none' }}>+447356083797</a>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>Copyright © 2026 ManaWave LTD All Rights Reserved</p>
          </div>
        </div>
      </footer>

      {/* Gentle Closure */}
      <footer className="bookings-footer">
        <div className="wave-footer-closure"></div>
      </footer>
    </div>
  );
}

export default MyBookings;



